import { useEffect, useMemo, useState } from "react";
import QRCode from "qrcode";
import {
  createDirectEstiaIntent,
  DIRECT_FLOW_CONFIG,
  getDirectEstiaIntentStatus,
} from "../services/estiaDirectFlow";
import { buildEstiaPosPaymentDeepLink } from "../services/paymentLinks";
import { buildTransactionId } from "../utils/buildTransactionId";
import { resolveDirectViewState } from "../utils/resolveViewState";

type Args = {
  open: boolean;
  amount: number;
};

export function useDirectCheckout({ open, amount }: Args) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string>("PENDING");
  const [timeRemaining, setTimeRemaining] = useState(DIRECT_FLOW_CONFIG.expirySeconds);
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [trackingId, setTrackingId] = useState<string | null>(null);
  const [intentId, setIntentId] = useState<string | null>(null);
  const [transactionId, setTransactionId] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  const viewState = useMemo(
    () => resolveDirectViewState(status, timeRemaining),
    [status, timeRemaining]
  );

  useEffect(() => {
    if (!open) return;

    async function startIntent() {
      setLoading(true);
      setError(null);
      setStatus("PENDING");
      setTimeRemaining(DIRECT_FLOW_CONFIG.expirySeconds);
      setTrackingId(null);
      setIntentId(null);
      try {
        const resolvedTransactionId = transactionId || buildTransactionId();
        if (!transactionId) {
          setTransactionId(resolvedTransactionId);
        }
        const { intentId, trackingId: createdTrackingId } = await createDirectEstiaIntent(
          amount,
          resolvedTransactionId
        );
        setIntentId(intentId);
        setTrackingId(createdTrackingId);
        const qr = await QRCode.toDataURL(buildEstiaPosPaymentDeepLink(intentId), {
          margin: 1,
          width: 320,
        });
        setQrDataUrl(qr);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to create intent.");
      } finally {
        setLoading(false);
      }
    }

    void startIntent();
  }, [amount, open, refreshTick, transactionId]);

  useEffect(() => {
    if (!open) {
      setTransactionId(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !trackingId) return;
    if (status === "PAID" || status === "SUCCESS" || status === "FAILED" || status === "FAILURE") return;

    const poll = globalThis.setInterval(async () => {
      try {
        const statusResponse = await getDirectEstiaIntentStatus(trackingId);
        if (statusResponse.status) {
          setStatus(statusResponse.status.toUpperCase());
        }
      } catch {
        // Keep UI responsive if status polling temporarily fails.
      }
    }, 2500);

    return () => globalThis.clearInterval(poll);
  }, [open, status, trackingId]);

  useEffect(() => {
    if (!open || loading || error) return;
    if (status === "PAID" || status === "SUCCESS" || status === "FAILED" || status === "FAILURE") {
      return;
    }
    const timer = globalThis.setInterval(() => {
      setTimeRemaining((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => globalThis.clearInterval(timer);
  }, [error, loading, open, status]);

  return {
    loading,
    error,
    qrDataUrl,
    appLaunchUrl: intentId ? buildEstiaPosPaymentDeepLink(intentId) : "",
    timeRemaining,
    viewState,
    refresh: () => setRefreshTick((prev) => prev + 1),
  };
}
